import { buildControllerRules } from "@gettactic/helpers/src/hook-form/buildControllerRules";
import { buildErrorMessageForName } from "@gettactic/helpers/src/hook-form/buildErrorMessageForName";
import React from "react";
import { FieldValues } from "react-hook-form";
import { NumberInput } from "./NumberInput";
import { HookFormNumberInputProps } from "./NumberInput.types";

/**
 * `NumberInput` registered in a react-hook-form form. The value is stored as a number
 * (`valueAsNumber`); `min` and `max` become rules with default or custom messages.
 */
export const HookFormNumberInput = <T extends FieldValues>(
  props: HookFormNumberInputProps<T>
): React.ReactElement => {
  const {
    form,
    name,
    rules,
    min,
    max,
    minErrorMessage,
    maxErrorMessage,
    ...rest
  } = props;

  const combinedRules = {
    ...rules,
    valueAsNumber: true,
    ...(min !== undefined && {
      min: {
        value: min,
        message: minErrorMessage || `Value must be at least ${min}`
      }
    }),
    ...(max !== undefined && {
      max: {
        value: max,
        message: maxErrorMessage || `Value must be at most ${max}`
      }
    })
  };

  return (
    <NumberInput
      {...form.register(
        name,
        buildControllerRules({
          form,
          name,
          rules: combinedRules,
          isRequired: props.isRequired
        })
      )}
      error={buildErrorMessageForName(form, name)}
      min={min}
      max={max}
      {...rest}
    />
  );
};
